import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";

const PHOTOS = [
    {
        src: "/images/office-2.jpg",
        alt: "Consultório do Dr. Gabriel Riccio — recepção e sala de espera",
    },
    {
        src: "/images/office-3.jpg",
        alt: "Consultório do Dr. Gabriel Riccio — sala de atendimento com equipamentos",
    },
];

export const Gallery = () => {
    const [active, setActive] = useState(null);

    const close = useCallback(() => setActive(null), []);
    const step = useCallback(
        (dir) => setActive((cur) => (cur + dir + PHOTOS.length) % PHOTOS.length),
        [],
    );

    useEffect(() => {
        if (active === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, close, step]);

    return (
        <section id="galeria" data-testid="gallery-section" className="bg-[#FAFCFF] py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeader
                    overline="O consultório"
                    title="Um ambiente preparado para cuidar de você"
                    subtitle="Conheça o espaço, os equipamentos e os detalhes pensados para o seu conforto."
                />
                <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {PHOTOS.map((photo, i) => (
                        <Reveal key={photo.src} delay={i * 0.1} className="h-full">
                            <button
                                type="button"
                                onClick={() => setActive(i)}
                                data-testid={`gallery-item-${i}`}
                                aria-label={`Ampliar foto: ${photo.alt}`}
                                className="group relative block h-80 w-full overflow-hidden rounded-3xl md:h-96"
                            >
                                <img
                                    src={photo.src}
                                    alt={photo.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <span className="absolute inset-0 bg-slate-900/0 transition-colors duration-300 group-hover:bg-slate-900/20" />
                                <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-800 opacity-0 shadow-md transition-all duration-300 group-hover:opacity-100">
                                    <Expand className="h-4 w-4" />
                                </span>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
                        onClick={close}
                        data-testid="gallery-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Visualização ampliada da foto do consultório"
                    >
                        <button
                            type="button"
                            onClick={close}
                            aria-label="Fechar visualização"
                            data-testid="lightbox-close-button"
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(-1);
                            }}
                            aria-label="Foto anterior"
                            data-testid="lightbox-prev-button"
                            className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <motion.img
                            key={PHOTOS[active].src}
                            src={PHOTOS[active].src}
                            alt={PHOTOS[active].alt}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35 }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
                            data-testid="lightbox-image"
                        />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(1);
                            }}
                            aria-label="Próxima foto"
                            data-testid="lightbox-next-button"
                            className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
